/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography, breakpoints } from '@coursera/cds-core';

import type {
  EntWebsiteBlockInsightsQuery,
  EntWebsiteInsightsFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import { entWebsiteBlockInsightsQuery } from 'bundles/ent-website/queries/EntWebsiteInsights';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';

import _t from 'i18n!nls/ent-website';

type Props = BlockModuleInnerProps<EntWebsiteInsightsFragment>;

const styles = {
  cardsContainer: () => css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-flow: row dense;
    grid-gap: 30px;
    margin-top: var(--cds-spacing-600);

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(1, 1fr);
    }
  `,

  cardContainer: css`
    width: 100%;
    display: flex;
    align-items: stretch;
  `,

  card: () => css`
    width: 100%;
    padding: var(--cds-spacing-400);
    background-color: var(--cds-color-blue-700);
    color: var(--cds-color-neutral-primary-invert) !important;
    border-radius: 8px;
  `,

  subheading: () => css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-400);
  `,

  cardHeadline: () => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-150);
  `,

  cardIcon: css`
    width: 65px;
    height: 65px;
    object-fit: contain;
  `,
};

export function InsightsModule({ content, assembly = {} }: Props) {
  if (!content) return null;

  const { heading, subheading, cardsCollection } = content;

  const cards = cardsCollection?.items ?? [];

  const contentPanel = () => (
    <div>
      <Typography variant="d2semibold">{heading}</Typography>
      <Typography variant="h2" css={styles.subheading}>
        {subheading}
      </Typography>
      <div css={styles.cardsContainer}>
        {cards?.map((card) => (
          <Grid css={styles.cardContainer} key={card?.headline}>
            <Grid css={styles.card}>
              {card?.icon?.url && (
                <img css={styles.cardIcon} src={card?.icon?.url} alt={card?.icon?.description ?? _t('Card Icon')} />
              )}
              {card?.headline && (
                <Typography color="invertBody" variant="h1semibold" css={styles.cardHeadline}>
                  {card?.headline}
                </Typography>
              )}
              {card?.body && (
                <Typography variant="h2" color="invertBody">
                  {card?.body}
                </Typography>
              )}
            </Grid>
          </Grid>
        ))}
      </div>
    </div>
  );

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockInsightsModule = withBlockQuery<EntWebsiteInsightsFragment, EntWebsiteBlockInsightsQuery>(
  entWebsiteBlockInsightsQuery
)(InsightsModule);
